import { css } from '@emotion/core';
import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import t from 'react-translate';
import { useForm, FormProvider, useFieldArray, useWatch, Control } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppDispatch } from 'redux/store';

import { each, pick, some } from 'underscore';
import NvFroala from 'froala/components/nv-froala';
import { ToolbarOptions, FroalaViewMode, UploadType } from 'froala/helpers/nv-froala-constants';
import { SanitizationLevel } from 'froala/helpers/sanitizer';
import NvStarRating from 'shared/components/nv-star-rating';
import { Button } from 'react-bootstrap';
import ClickableContainer from 'components/clickable-container';
import NvIcon from 'shared/components/nv-icon';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { SkillRatingsParams } from 'redux/schemas/api/video-practice';
import { useSelector } from 'react-redux';
import { addRating } from 'redux/actions/video-practice';
import { SkillTag } from 'redux/schemas/models/skill-tag';

import { config } from '../../../../config/pendo.config.json';

interface SkillRatingProps {
  skillTags?: SkillTag[]
  showForm: Dispatch<SetStateAction<boolean>>;
  submissionId: number
  onSubmit?: () => void
}

type RatingData = {
  id: number,
  name: string,
  body?: string,
  rating?: number
};

type FormData = {
  ratings: RatingData[]
};

interface AddSkillRatingProps {
  control: Control<FormData>
  index: number
}

const getInitialFormData = (tags: SkillTag[]): RatingData[] => {
  if (tags?.length) {
    const ratings = [];
    each(tags, tag => {
      ratings.push({ ...tag, rating: 0, body: '' });
    });

    return ratings;
  }
  return [];
};

const styles = css`
  .tag-badge {
    border-radius: 20px;
  }
`;

const AddSkillRating = ({ control, index }: AddSkillRatingProps) => {
  const { rating, name } = useWatch({ control, name: `ratings.${index}` });

  const froalaRef = useRef(null);
  useEffect(() => {
    if (rating > 0) {
      froalaRef.current?.focus();
    }
  }, [rating]);

  return (
    <div className='d-flex flex-column my-4'>
      <div className='d-flex justify-content-between'>
        <div className='tag-badge bg-gray-7 py-2 px-3 text-black'>
          {name}
        </div>
        <div>
          <NvStarRating
            starSize='medium'
            name={`ratings.${index}.rating`}
            withForm
            tooltipText={t.PRACTICE_ROOM.SKILL_FEEDBACK.REMOVE_RATING()}
            tooltipIndex={rating}
          />
        </div>
      </div>
      <div className={rating > 0 ? 'mt-4' : 'd-none'}>
        <NvFroala
          withForm
          toolbarButtons={[ToolbarOptions.BOLD, ToolbarOptions.ITALIC, ToolbarOptions.UNDERLINE]}
          name={`ratings.${index}.body`}
          placeholder={t.PRACTICE_ROOM.SKILL_FEEDBACK.PLACEHOLDER()}
          className='text-body'
          sanitizationLevel={SanitizationLevel.SIMPLE}
          preset={FroalaViewMode.INLINE}
          uploadType={UploadType.NONE}
          minHeight={100}
          ref={froalaRef}
        />
      </div>
    </div>
  );
};

const SkillFeedback = ({
  showForm,
  submissionId,
  skillTags,
  onSubmit: onSubmitFeedback,
}: SkillRatingProps) => {
  const dispatch = useAppDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const currentInstitutionId = useSelector(
    (state) => state.app.currentInstitutionId,
  );

  const ratingSchema = yup.object().shape({
    rating: yup.number().required(),
    body: yup.string().nullable(),
  });
  const validationSchema = yup.object().shape({
    ratings: yup.array().of(ratingSchema),
  }).test(
    'rating',
    '',
    (ratings) => some(ratings.ratings, item => item.rating > 0),
  );

  const methods = useForm<FormData>({
    mode: 'onChange',
    defaultValues: { ratings: getInitialFormData(skillTags) },
    resolver: yupResolver(validationSchema),
  });

  const { fields } = useFieldArray<FormData, 'ratings', 'id'>({
    name: 'ratings',
    control: methods.control,
    keyName: 'id',
  });

  const { handleSubmit, formState } = methods;

  const { isDirty, isValid } = formState;

  const onSubmit = (data: FormData) => {
    const submitData = () => {
      const pickedData = data?.ratings?.map(item => ({
        skillTagId: item.id,
        body: item.body,
        rating: item.rating,
      })).filter(rating => (rating.rating > 0));
      const skillRatings: SkillRatingsParams = {
        institutionId: currentInstitutionId,
        ratings: pickedData,
        ownerId: submissionId,
        ownerType: 'VideoPracticeSubmission',
      };
      dispatch(addRating(skillRatings)).then(() => {
        setIsSubmitting(false);
        onSubmitFeedback();
        showForm(false);
      });
    };

    setIsSubmitting(true);
    dispatch(openConfirmationDialog({
      onConfirm: () => submitData(),
      onCancel: () => setIsSubmitting(false),
      cancelText: t.FORM.CANCEL(),
      confirmText: t.FORM.YES_SURE(),
      title: t.PRACTICE_ROOM.SKILL_FEEDBACK.SUBMIT_CONFIRMATION.TITLE(),
      bodyText: t.PRACTICE_ROOM.SKILL_FEEDBACK.SUBMIT_CONFIRMATION.DESCRIPTION(),
      confirmButtonVariant: 'primary',
      icon: 'info',
    }));
  };

  const onCancel = () => {
    if (isDirty) {
      dispatch(openConfirmationDialog({
        onConfirm: () => showForm(false),
        cancelText: t.FORM.CANCEL(),
        confirmText: t.FORM.DISCARD_CHANGES(),
        title: t.FORM.UNSAVED_CHANGES.NAVIGATE_AWAY_CHANGES(),
      }));
    } else {
      showForm(false);
    }
  };

  return (
    <div className='d-flex flex-column m-4' css={styles}>
      <div className='d-flex justify-content-between text-large-body font-weight-bolder mb-4'>
        {t.PRACTICE_ROOM.SKILL_FEEDBACK.HEADER()}
        <ClickableContainer
          className='scenarios-close'
          onClick={onCancel}
          aria-label={t.NOVOED.CLOSE()}
        >
          <NvIcon size='small' icon='close' className='text-gray-1' />
        </ClickableContainer>
      </div>
      <span>{t.PRACTICE_ROOM.SKILL_FEEDBACK.DESCRIPTION()}</span>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='p-2 mt-2 w-100'>
            {fields.map((skill, index) => (
              <AddSkillRating control={methods.control} index={index} key={skill.id} />
            ))}
          </div>
          <div className='button-bar mt-5'>
            <Button
              variant='secondary'
              onClick={onCancel}
            >
              {t.FORM.CANCEL()}
            </Button>
            <Button
              variant='primary'
              disabled={!isValid || !isDirty || isSubmitting}
              type='submit'
              pendo-tag-name={config.pendo.skillsFeedback.practiceSkillsFeedbackSubmit}
            >
              {isSubmitting ? t.FORM.SUBMITTING() : t.FORM.SUBMIT()}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default SkillFeedback;
